'use client';

import { submitFormToCRM } from '@/app/actions/form-actions';
import { setScheduleHandoffCookie } from '@/app/actions/schedule-callback-actions';
import { URLS } from '@/app/config/env';
import { DEFAULT_DF_APP_ID } from '@/app/config/profile-builder';
import { convertNumericDiscountToWords } from '@/app/utils/discount-converter';
import { getVisitorId } from '@/app/utils/visitor-tracking';

let leadTrackingPromise: Promise<typeof import('@varsitytutors/lead-tracking')> | undefined;

export const loadLeadTracking = () => {
  leadTrackingPromise ??= import('@varsitytutors/lead-tracking');
  return leadTrackingPromise;
};

/** One id per page view, shared by the CRM's CAPI event and the browser Pixel Lead (as InteractiveForm). */
export const leadEventId = async (): Promise<string> => {
  try {
    const { initializeEventId } = await loadLeadTracking();
    return String(initializeEventId('Lead'));
  } catch {
    // CRM submission must not depend on a deferred analytics chunk loading.
    return `lead-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
};

async function awaitMarketingVT(maxMs = 300): Promise<void> {
  if (window.MarketingVT?.getAttribution) return;
  const start = Date.now();
  while (Date.now() - start < maxMs) {
    if (window.MarketingVT?.getAttribution) return;
    await new Promise((resolve) => setTimeout(resolve, 25));
  }
}

/** The chat's thank-you page (app/test-prep/thank-you). */
const CHAT_THANK_YOU_URL = '/df/test-prep/thank-you';

export type SubmitResult = { success: true; redirectUrl: string } | { success: false; error: string };

/**
 * Submits a chat lead through the same server action, tracking and thank-you routing as
 * InteractiveForm.handleFormSubmission, so chat and form leads are counted, deduplicated (Pixel Lead
 * + CAPI share `eventId`) and qualified the same way. Keep this in step with InteractiveForm. The
 * caller does the redirect (after clearing the chat's saved answers).
 */
export async function submitChatLead(answers: Record<string, string>, eventId: string, subject: string): Promise<SubmitResult> {
  await awaitMarketingVT(300);
  if (!window.MarketingVT) {
    const { captureMissingMarketingAttribution } = await import('@/app/utils/marketing-attribution-observability');
    captureMissingMarketingAttribution({
      origin: 'paid-leadform',
      page_url: window.location.href,
      user_agent: navigator.userAgent,
    });
  }

  const visitorId = getVisitorId();
  const result = await submitFormToCRM({
    form_variant: 'chat',
    answers,
    visitorId,
    schoolDistrict: answers['school-district'] || '',
    referer: document.referrer?.trim() ? document.referrer : URLS.DEFAULT_REFERER,
    // The slug pages pass no district discounts: every district gets the server's default.
    discount_in_words: convertNumericDiscountToWords(undefined),
    application_id: DEFAULT_DF_APP_ID,
    page_url: window.location.href,
    attribution: window.MarketingVT?.getAttribution?.(),
    event_id: eventId,
    persistentVisitorId: window.MarketingVT?.getPersistentVisitorId?.(),
  });
  if (!result.success) {
    return { success: false, error: result.error || 'Something went wrong. Please try again.' };
  }

  try {
    const { trackLeadCreationSuccess } = await import('@/app/utils/web-analytics');
    await trackLeadCreationSuccess('no_subject_labor_ill_va_districts');
  } catch {
    // Do not block thank-you routing after CRM has accepted the lead.
  }
  try {
    const { pushToDataLayer, trackMetaEvent } = await loadLeadTracking();
    pushToDataLayer('lead_created', {
      leadData: {
        leadId: 'unknown',
        timestamp: new Date().toISOString(),
        source: window.location.href,
        visitorId,
      },
    });
    await trackMetaEvent(
      'Lead',
      { content_category: subject || 'general' },
      {
        userData: {
          email: answers.email,
          phone: answers.phone,
          fullName: answers['full-name'],
          zipCode: answers.zipcode,
        },
        eventId,
      }
    );
  } catch {
    // Do not block thank-you routing after CRM has accepted the lead.
  }

  // After hours: the in-app scheduling page. During hours (or missing phone/zip): the thank-you page.
  const phone = answers.phone?.replace(/\D/g, '') || '';
  const zip = answers.zipcode?.trim() || '';
  let afterHours = false;
  if (phone && zip) {
    try {
      const [{ isForceAfterHoursEnabled }, { isAfterHoursForZip }] = await Promise.all([import('@/app/utils/after-hours-dev'), import('@/app/utils/business-hours')]);
      afterHours = isForceAfterHoursEnabled(new URLSearchParams(window.location.search)) || isAfterHoursForZip(zip);
    } catch {
      afterHours = false;
    }
  }
  const params = new URLSearchParams(window.location.search).toString();
  if (afterHours) {
    await setScheduleHandoffCookie(phone, zip);
    return { success: true, redirectUrl: params ? `/df/thank-you-after-hours?${params}` : '/df/thank-you-after-hours' };
  }
  // During hours: the chat's own thank-you page, with the call countdown (keeping the query string, so the
  // local number and attribution carry over).
  return { success: true, redirectUrl: params ? `${CHAT_THANK_YOU_URL}?${params}` : CHAT_THANK_YOU_URL };
}
